import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { uploadFile } from '../utils/fileUpload';
import { Plus, FileText, Building2, Calendar, Download, CreditCard as Edit, Trash2, Eye, Search, Filter, AlertTriangle, CheckCircle, Clock, Upload, X, Users, PenTool } from 'lucide-react';

interface SWMS {
  id: string;
  title: string;
  site_id: string;
  activity_description: string;
  hazards: string[];
  control_measures: string[];
  ppe_required: string[];
  emergency_procedures: string;
  approval_status: 'draft' | 'pending' | 'approved' | 'rejected';
  approved_by?: string;
  approved_date?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  version: number;
}

interface Site {
  id: string;
  name: string;
  address: string;
  type: string;
  status: string;
}

interface SWMSWithSite extends SWMS {
  site?: Site;
}

export const SWMSPage: React.FC = () => {
  const [swmsList, setSWMSList] = useState<SWMSWithSite[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSite, setFilterSite] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedSiteForUpload, setSelectedSiteForUpload] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [settingUpStorage, setSettingUpStorage] = useState(false);
  const [showSignaturesModal, setShowSignaturesModal] = useState(false);
  const [selectedSWMSForSignatures, setSelectedSWMSForSignatures] = useState<SWMSWithSite | null>(null);
  const [swmsSignatures, setSWMSSignatures] = useState<any[]>([]);
  const [loadingSignatures, setLoadingSignatures] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>('demolition_swms');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      await Promise.all([
        fetchSWMS(),
        fetchSites()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async () => {
    try {
      const { data, error } = await supabase
        .from('sites')
        .select('id, name, address, type, status')
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      setSites(data || []);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const fetchSWMS = async () => {
    try {
      const { data, error } = await supabase
        .from('swms')
        .select(`
          *,
          sites!inner(id, name, address, type, status)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const swmsWithSites: SWMSWithSite[] = (data || []).map(item => ({
        ...item,
        site: item.sites
      }));

      setSWMSList(swmsWithSites);
    } catch (error) {
      console.error('Error fetching SWMS:', error);
    }
  };

  const setupStorageAutomatically = async () => {
    setSettingUpStorage(true);
    try {
      console.log('Setting up SWMS storage automatically...');
      
      // Get current user session for authorization
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('User not authenticated');
      }

      // Call our edge function to set up storage
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/setup-swms-storage`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to set up storage');
      }

      const result = await response.json();
      console.log('Storage setup result:', result);
      
      alert('Storage setup completed successfully! You can now upload SWMS documents.');
      
    } catch (error) {
      console.error('Storage setup error:', error);
      alert(`Failed to set up storage: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSettingUpStorage(false);
    }
  };

  const handleViewSignatures = async (swms: SWMSWithSite) => {
    setSelectedSWMSForSignatures(swms);
    setLoadingSignatures(true);
    setShowSignaturesModal(true);
    
    try {
      // Get all SWMS documents for this site
      const { data: siteSWMSData, error: siteSWMSError } = await supabase
        .from('swms')
        .select('id')
        .eq('site_id', swms.site_id);

      if (siteSWMSError) throw siteSWMSError;

      const swmsIds = siteSWMSData?.map(s => s.id) || [swms.id];

      // Get signatures for ALL SWMS documents on this site
      const { data, error } = await supabase
        .from('swms_signatures')
        .select(`
          *,
          workers!inner(id, name, role, email, phone, certifications, whitecard_number)
        `)
        .in('swms_id', swmsIds)
        .eq('acknowledged', true)
        .order('signed_at', { ascending: false });

      if (error) throw error;
      
      // Remove duplicates by worker_id (keep the most recent signature)
      const uniqueSignatures = [];
      const seenWorkers = new Set();
      
      for (const signature of data || []) {
        if (!seenWorkers.has(signature.worker_id)) {
          uniqueSignatures.push(signature);
          seenWorkers.add(signature.worker_id);
        }
      }
      
      setSWMSSignatures(uniqueSignatures);
    } catch (error) {
      console.error('Error fetching SWMS signatures:', error);
      setSWMSSignatures([]);
    } finally {
      setLoadingSignatures(false);
    }
  };

  const generateSignaturesPDF = async () => {
    if (!selectedSWMSForSignatures || swmsSignatures.length === 0) return;

    // Fetch all SWMS documents for this site
    const { data: siteSWMSDocuments, error: swmsError } = await supabase
      .from('swms')
      .select('*')
      .eq('site_id', selectedSWMSForSignatures.site_id)
      .order('created_at', { ascending: false });

    if (swmsError) {
      console.error('Error fetching SWMS documents:', swmsError);
      return;
    }

    const allSWMSDocuments = siteSWMSDocuments || [];

    // Create a temporary container for the PDF content
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '794px'; // A4 width in pixels at 96 DPI
    container.style.backgroundColor = 'white';
    container.style.padding = '30px';
    container.style.fontFamily = 'Arial, sans-serif';
    container.style.lineHeight = '1.4';
    container.style.fontSize = '12px';
    
    // Create the HTML content
    container.innerHTML = `
      <div style="max-width: 734px; margin: 0 auto;">
        <!-- Header -->
        <div style="text-align: center; margin-bottom: 25px; border-bottom: 3px solid #ea580c; padding-bottom: 20px;">
          <div style="display: flex; align-items: center; justify-content: center; margin-bottom: 15px;">
            <div style="width: 50px; height: 50px; background-color: #fed7aa; border-radius: 10px; display: flex; align-items: center; justify-content: center; margin-right: 15px;">
              <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="#ea580c" stroke-width="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10,9 9,9 8,9"/>
              </svg>
            </div>
            <div>
              <h1 style="font-size: 26px; font-weight: bold; color: #1f2937; margin: 0; line-height: 1.1;">SWMS ACKNOWLEDGMENT REPORT</h1>
              <p style="font-size: 14px; color: #6b7280; margin: 5px 0 0 0;">Safe Work Method Statement Signatures</p>
            </div>
          </div>
        </div>

        <!-- SWMS Information Banner -->
        <div style="background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); color: white; border-radius: 10px; padding: 20px; margin-bottom: 25px;">
          <div style="display: grid; grid-template-columns: 2fr 1fr; gap: 20px; align-items: center;">
            <div>
              <h2 style="font-size: 22px; font-weight: bold; margin: 0 0 8px 0;">${selectedSWMSForSignatures.title}</h2>
              <p style="font-size: 14px; margin: 0 0 5px 0; opacity: 0.9;">📍 ${selectedSWMSForSignatures.site?.name || 'Unknown Site'}</p>
              <p style="font-size: 13px; margin: 0; opacity: 0.8;">
                ${selectedSWMSForSignatures.site?.address || 'No address available'}
              </p>
            </div>
            <div style="text-align: right;">
              <div style="background-color: rgba(255,255,255,0.2); border-radius: 8px; padding: 12px;">
                <p style="font-size: 24px; font-weight: bold; margin: 0; line-height: 1;">${swmsSignatures.length}</p>
                <p style="font-size: 12px; margin: 0; opacity: 0.9;">Worker Acknowledgments</p>
              </div>
            </div>
          </div>
        </div>

        <!-- SWMS Details -->
        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 18px; margin-bottom: 25px;">
          <h3 style="font-size: 16px; font-weight: 600; color: #1f2937; margin: 0 0 15px 0;">SWMS Details</h3>

          ${allSWMSDocuments.length > 0 ? `
            <div style="margin-bottom: 15px;">
              <h4 style="font-size: 12px; font-weight: 600; color: #374151; margin: 0 0 10px 0;">Site SWMS Documents (${allSWMSDocuments.length})</h4>
              <div style="display: grid; gap: 10px;">
                ${allSWMSDocuments.map((doc, index) => `
                  <div style="background-color: white; border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px;">
                    <div style="display: flex; align-items: center; justify-content: space-between;">
                      <div style="flex: 1;">
                        <div style="display: flex; align-items: center; gap: 8px;">
                          <span style="font-size: 11px; font-weight: 600; color: #1f2937;">${index + 1}. ${doc.title}</span>
                          <span style="font-size: 9px; padding: 2px 6px; border-radius: 4px; background-color: ${doc.approval_status === 'approved' ? '#dcfce7' : '#fef3c7'}; color: ${doc.approval_status === 'approved' ? '#166534' : '#92400e'}; text-transform: capitalize;">
                            ${doc.approval_status}
                          </span>
                        </div>
                        <p style="font-size: 10px; color: #6b7280; margin: 3px 0 0 0;">
                          ${doc.activity_description.substring(0, 100)}${doc.activity_description.length > 100 ? '...' : ''}
                        </p>
                        <div style="display: flex; gap: 15px; margin-top: 5px;">
                          <span style="font-size: 9px; color: #9ca3af;">Version: v${doc.version}</span>
                          <span style="font-size: 9px; color: #9ca3af;">Type: ${doc.document_type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}</span>
                          <span style="font-size: 9px; color: #9ca3af;">Created: ${new Date(doc.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                `).join('')}
              </div>
            </div>
          ` : ''}

          <div style="border-top: 1px solid #e2e8f0; padding-top: 15px; margin-top: 15px;">
            <h4 style="font-size: 12px; font-weight: 600; color: #374151; margin: 0 0 10px 0;">Primary Document Details</h4>
            <div style="grid-template-columns: repeat(2, 1fr); gap: 15px; display: grid;">
              <div>
                <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Activity Description</label>
                <p style="font-size: 11px; color: #1f2937; margin: 0; line-height: 1.4;">${selectedSWMSForSignatures.activity_description}</p>
              </div>
              <div>
                <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Approval Status</label>
                <p style="font-size: 11px; margin: 0; text-transform: capitalize; font-weight: 600; color: ${selectedSWMSForSignatures.approval_status === 'approved' ? '#16a34a' : '#d97706'};">
                  ${selectedSWMSForSignatures.approval_status}
                </p>
              </div>
              <div>
                <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Version</label>
                <p style="font-size: 11px; color: #1f2937; margin: 0;">v${selectedSWMSForSignatures.version}</p>
              </div>
              <div>
                <label style="font-size: 10px; font-weight: 600; color: #374151; display: block; margin-bottom: 2px;">Created</label>
                <p style="font-size: 11px; color: #1f2937; margin: 0;">${new Date(selectedSWMSForSignatures.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Worker Acknowledgments -->
        <div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; margin-bottom: 25px;">
          <h3 style="font-size: 18px; font-weight: 600; color: #1f2937; margin: 0 0 18px 0; display: flex; align-items: center;">
            <span style="margin-right: 8px;">✅</span> Worker Acknowledgments
          </h3>
          
          <div style="display: grid; gap: 15px;">
            ${swmsSignatures.map((signature, index) => `
              <div style="background-color: white; border: 1px solid #e5e7eb; border-radius: 6px; padding: 15px;">
                <div style="display: grid; grid-template-columns: auto 1fr auto; gap: 15px; align-items: center;">
                  
                  <!-- Index Number -->
                  <div style="width: 30px; height: 30px; background-color: #dcfce7; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
                    <span style="font-size: 12px; font-weight: bold; color: #166534;">${index + 1}</span>
                  </div>
                  
                  <!-- Worker Information -->
                  <div>
                    <p style="font-size: 14px; font-weight: 600; color: #1f2937; margin: 0 0 3px 0;">${signature.workers?.name || 'Unknown Worker'}</p>
                    <p style="font-size: 11px; color: #6b7280; margin: 0 0 2px 0; text-transform: capitalize;">${signature.workers?.role?.replace('_', ' ') || 'N/A'}</p>
                    <p style="font-size: 10px; color: #9ca3af; margin: 0;">${signature.workers?.email || 'No email'}</p>
                    ${signature.workers?.phone ? `<p style="font-size: 10px; color: #9ca3af; margin: 0;">📞 ${signature.workers.phone}</p>` : ''}
                    ${signature.workers?.whitecard_number ? `<p style="font-size: 9px; color: #374151; margin: 2px 0 0 0;">WC: ${signature.workers.whitecard_number}</p>` : ''}
                  </div>
                  
                  <!-- Signature Date -->
                  <div style="text-align: center;">
                    <p style="font-size: 11px; font-weight: 600; color: #374151; margin: 0 0 2px 0;">Acknowledged</p>
                    <p style="font-size: 10px; color: #6b7280; margin: 0;">${signature.signed_at ? new Date(signature.signed_at).toLocaleDateString() : 'N/A'}</p>
                  </div>
                </div>
                
                <!-- Signature Preview (if available) -->
                ${signature.signature ? `
                  <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #f3f4f6;">
                    <div style="display: flex; align-items: center; justify-content: space-between;">
                      <span style="font-size: 10px; color: #6b7280; font-weight: 500;">Digital Signature:</span>
                      <div style="background-color: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; padding: 4px 8px; max-width: 200px; height: 30px; display: flex; align-items: center; justify-content: center;">
                        ${signature.signature.startsWith('data:image') ? 
                          `<img src="${signature.signature}" alt="Signature" style="max-width: 100%; max-height: 25px;" />` :
                          `<span style="font-size: 10px; color: #374151; font-style: italic;">${signature.signature.substring(0, 30)}${signature.signature.length > 30 ? '...' : ''}</span>`
                        }
                      </div>
                    </div>
                  </div>
                ` : ''}
              </div>
            `).join('')}
          </div>
        </div>

        <!-- Compliance Summary -->
        <div style="background-color: #f0fdf4; border: 1px solid #22c55e; border-radius: 8px; padding: 18px; margin-bottom: 20px;">
          <h3 style="font-size: 16px; font-weight: 600; color: #166534; margin: 0 0 12px 0; display: flex; align-items: center;">
            <span style="margin-right: 8px;">🛡️</span> Compliance Summary
          </h3>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px;">
            <div>
              <p style="font-size: 12px; color: #166534; margin: 0 0 8px 0; font-weight: 600;">✓ All workers have acknowledged the SWMS</p>
              <p style="font-size: 12px; color: #166534; margin: 0 0 8px 0; font-weight: 600;">✓ Digital signatures captured for verification</p>
              <p style="font-size: 12px; color: #166534; margin: 0; font-weight: 600;">✓ Work method statements reviewed and accepted</p>
            </div>
            <div>
              <p style="font-size: 12px; color: #166534; margin: 0 0 8px 0; font-weight: 600;">✓ Safety procedures understood</p>
              <p style="font-size: 12px; color: #166534; margin: 0 0 8px 0; font-weight: 600;">✓ Risk controls acknowledged</p>
              <p style="font-size: 12px; color: #166534; margin: 0; font-weight: 600;">✓ Emergency procedures confirmed</p>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div style="text-align: center; padding-top: 20px; border-top: 2px solid #e5e7eb;">
          <p style="font-size: 11px; color: #6b7280; margin: 0 0 5px 0; line-height: 1.4;">
            <strong>SWMS Acknowledgment Report</strong><br>
            This document certifies that all listed workers have reviewed and acknowledged<br>
            the Safe Work Method Statement and understand the safety requirements.
          </p>
          <p style="font-size: 9px; color: #9ca3af; margin: 0; line-height: 1.3;">
            Generated: ${new Date().toLocaleString()} | SafetyPortal SWMS Management System<br>
            Report ID: ${selectedSWMSForSignatures.id.slice(0, 8)} | Total Acknowledgments: ${swmsSignatures.length}
          </p>
        </div>
      </div>
    `;

    // Add the container to the document
    document.body.appendChild(container);

    try {
      // Import html2canvas and jsPDF dynamically
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).jsPDF;

      // Convert the HTML to canvas
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      // Create PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);
      const contentHeight = pageHeight - (margin * 2);

      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;

      // Calculate scale to fit width perfectly
      const scale = (contentWidth * 3.78) / canvasWidth;

      // Calculate dimensions
      const imgWidth = contentWidth;
      const imgHeight = (canvasHeight * scale) / 3.78;

      const imgData = canvas.toDataURL('image/png');

      // Add pages as needed
      let yPosition = 0;
      let pageNumber = 0;

      while (yPosition < imgHeight) {
        if (pageNumber > 0) {
          pdf.addPage();
        }

        // Calculate the portion of the image to show on this page
        const sourceY = (yPosition * 3.78) / scale;
        const sourceHeight = Math.min((contentHeight * 3.78) / scale, canvasHeight - sourceY);
        const displayHeight = Math.min(contentHeight, imgHeight - yPosition);

        // Create a temporary canvas for this page's content
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvasWidth;
        pageCanvas.height = sourceHeight;
        const pageContext = pageCanvas.getContext('2d');

        if (pageContext) {
          pageContext.drawImage(
            canvas,
            0, sourceY,
            canvasWidth, sourceHeight,
            0, 0,
            canvasWidth, sourceHeight
          );

          const pageImgData = pageCanvas.toDataURL('image/png');
          pdf.addImage(pageImgData, 'PNG', margin, margin, imgWidth, displayHeight);
        }

        yPosition += contentHeight;
        pageNumber++;
      }

      // Generate filename
      const swmsTitle = selectedSWMSForSignatures.title.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
      const filename = `swms-signatures-${swmsTitle}-${new Date().toISOString().split('T')[0]}.pdf`;

      pdf.save(filename);

    } finally {
      document.body.removeChild(container);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!selectedSiteForUpload) {
      alert('Please select a site first');
      return;
    }

    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file only');
      return;
    }

    setUploadingFile(true);
    try {
      // Get current user session for database operations
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error('User session not found');
      }

      // Use the reusable upload utility
      const result = await uploadFile(file, 'swms-documents', 'swms');
      
      if (!result.success || !result.url) {
        throw new Error(result.error || 'Upload failed');
      }

      // Get site information for the title
      const selectedSite = sites.find(site => site.id === selectedSiteForUpload);
      
      // Create title based on document type
      const documentTypeLabels = {
        'demolition_swms': 'Demolition SWMS',
        'asbestos_swms': 'Asbestos SWMS', 
        'asbestos_removal_control_plan': 'Asbestos Removal Control Plan'
      };
      
      const title = `${documentTypeLabels[selectedDocumentType as keyof typeof documentTypeLabels]} - ${selectedSite?.name || 'Unknown Site'}`;

      // Prepare the SWMS record
      const swmsRecord = {
        title: title,
        site_id: selectedSiteForUpload,
        activity_description: `Uploaded SWMS document for ${selectedSite?.name}`,
        document_type: selectedDocumentType,
        hazards: [],
        control_measures: [],
        ppe_required: [],
        emergency_procedures: 'Refer to uploaded SWMS document',
        approval_status: 'pending' as const,
        created_by: session.user.id,
        pdf_url: result.url,
        version: 1
      };

      // Create SWMS record in database
      const { data: insertData, error: dbError } = await supabase
        .from('swms')
        .insert([swmsRecord])
        .select();

      if (dbError) {
        throw new Error(`Failed to save SWMS record: ${dbError.message}`);
      }

      // Refresh the SWMS list
      await fetchSWMS();
      
      // Close modal and reset
      setShowUploadModal(false);
      setSelectedSiteForUpload('');
      
      alert('SWMS uploaded successfully!');
    } catch (error) {
      console.error('Error uploading SWMS:', error);
      alert(`Failed to upload SWMS: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteSWMS = async (swmsId: string, pdfUrl?: string) => {
    try {
      // First, delete the PDF file from storage if it exists
      if (pdfUrl) {
        // Extract filename from the signed URL or use the SWMS ID
        const urlParts = pdfUrl.split('/');
        const fileName = urlParts[urlParts.length - 1].split('?')[0]; // Remove query parameters
        
        const { error: storageError } = await supabase.storage
          .from('swms-documents')
          .remove([fileName]);
        
        if (storageError) {
          console.error('Error deleting file from storage:', storageError);
          // Continue with database deletion even if file deletion fails
        }
      }
      
      // Delete the SWMS record from database
      const { error: dbError } = await supabase
        .from('swms')
        .delete()
        .eq('id', swmsId);
      
      if (dbError) {
        throw new Error(`Failed to delete SWMS record: ${dbError.message}`);
      }
      
      // Refresh the SWMS list
      await fetchSWMS();
      
      alert('SWMS deleted successfully!');
    } catch (error) {
      console.error('Error deleting SWMS:', error);
      alert(`Failed to delete SWMS: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'draft':
        return <FileText className="h-4 w-4 text-gray-600" />;
      case 'rejected':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      default:
        return <FileText className="h-4 w-4 text-gray-600" />;
    }
  };
  const getDocumentTypeLabel = (documentType: string) => {
    const labels = {
      'demolition_swms': 'Demolition SWMS',
      'asbestos_swms': 'Asbestos SWMS',
      'asbestos_removal_control_plan': 'Asbestos Removal Control Plan'
    };
    return labels[documentType as keyof typeof labels] || documentType;
  };

  const getDocumentTypeColor = (documentType: string) => {
    const colors = {
      'demolition_swms': 'bg-blue-100 text-blue-800',
      'asbestos_swms': 'bg-red-100 text-red-800',
      'asbestos_removal_control_plan': 'bg-purple-100 text-purple-800'
    };
    return colors[documentType as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const filteredSWMS = swmsList.filter(swms => {
    const matchesSearch = swms.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         swms.activity_description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSite = !filterSite || swms.site_id === filterSite;
    const matchesStatus = !filterStatus || swms.approval_status === filterStatus;
    return matchesSearch && matchesSite && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Safe Work Method Statements</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage SWMS documents for all site activities and work procedures
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Select Site:</label>
            <select
              value={selectedSiteForUpload}
              onChange={(e) => setSelectedSiteForUpload(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">Choose a site</option>
              {sites.map(site => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Document Type:</label>
            <select
              value={selectedDocumentType}
              onChange={(e) => setSelectedDocumentType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="demolition_swms">Demolition SWMS</option>
              <option value="asbestos_swms">Asbestos SWMS</option>
              <option value="asbestos_removal_control_plan">Asbestos Removal Control Plan</option>
            </select>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            disabled={!selectedSiteForUpload}
            className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Upload className="h-5 w-5" />
            <span>Upload SWMS</span>
          </button>
        </div>
      </div>


      {/* SWMS Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredSWMS.map((swms) => (
          <div
            key={swms.id} 
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-gray-900 line-clamp-2">{swms.title}</h3>
                  <p className="text-sm text-gray-500">{swms.site?.name}</p>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${getDocumentTypeColor(swms.document_type || 'demolition_swms')}`}>
                    {getDocumentTypeLabel(swms.document_type || 'demolition_swms')}
                  </span>
                </div>
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => {
                    if (swms.pdf_url) {
                      window.open(swms.pdf_url, '_blank');
                    } else {
                      alert('No PDF document available for this SWMS');
                    }
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600"
                  title="View SWMS"
                >
                  <Eye className="h-4 w-4" />
                </button>
                <button
                  onClick={() => {
                    if (confirm(`Are you sure you want to delete "${swms.title}"? This action cannot be undone.`)) {
                      handleDeleteSWMS(swms.id, swms.pdf_url);
                    }
                  }}
                  className="p-1 text-gray-400 hover:text-red-600"
                  title="Delete SWMS"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Status</span>
                <span className="text-sm text-gray-600">Active</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Version</span>
                <span className="text-sm text-gray-600">v{swms.version}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Created</span>
                <span className="text-sm text-gray-600">
                  {new Date(swms.created_at).toLocaleDateString()}
                </span>
              </div>

              {swms.approved_date && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Approved</span>
                  <span className="text-sm text-gray-600">
                    {new Date(swms.approved_date).toLocaleDateString()}
                  </span>
                </div>
              )}

              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-600 line-clamp-2">
                  {swms.activity_description}
                </p>
              </div>

              {swms.hazards && swms.hazards.length > 0 && (
                <div>
                  <span className="text-xs font-medium text-gray-700">Key Hazards:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {swms.hazards.slice(0, 2).map((hazard, index) => (
                      <span key={index} className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                        {hazard}
                      </span>
                    ))}
                    {swms.hazards.length > 2 && (
                      <span className="text-xs text-gray-500">+{swms.hazards.length - 2} more</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex space-x-2 mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={() => {
                  if (swms.pdf_url) {
                    window.open(swms.pdf_url, '_blank');
                  } else {
                    alert('No PDF document available for download');
                  }
                }}
                className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                disabled={!swms.pdf_url}
              >
                <Download className="h-4 w-4" />
                <span>Download</span>
              </button>
              <button
                onClick={() => handleViewSignatures(swms)}
                className="flex items-center justify-center space-x-1 px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm"
                title="View Worker Signatures"
              >
                <Users className="h-4 w-4" />
                <span>Signatures</span>
              </button>
              <Link
                to={`/sites/${swms.site_id}`}
                className="flex items-center justify-center px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                title="View Site"
              >
                <Building2 className="h-4 w-4" />
              </Link>
            </div>
          </div>
        ))}
      </div>

      {filteredSWMS.length === 0 && (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || filterSite || filterStatus ? 'No matching SWMS found' : 'No SWMS documents'}
          </h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || filterSite || filterStatus 
              ? 'Try adjusting your search or filter criteria.'
              : 'Upload SWMS documents using the dropdown above.'
            }
          </p>
          {(searchTerm || filterSite || filterStatus) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterSite('');
                setFilterStatus('');
              }}
              className="text-orange-600 hover:text-orange-700 text-sm font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Upload className="h-5 w-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">Upload SWMS Document</h3>
              </div>
              <button
                onClick={() => setShowUploadModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Selected Site:</strong> {sites.find(s => s.id === selectedSiteForUpload)?.name}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Document Type:</strong> {getDocumentTypeLabel(selectedDocumentType)}
                </p>
                <p className="text-sm text-gray-500">
                  Upload a PDF file containing the {getDocumentTypeLabel(selectedDocumentType)} for this site.
                </p>
              </div>

              {/* Drag and Drop Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive 
                    ? 'border-orange-500 bg-orange-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="flex flex-col items-center space-y-4">
                  <div className="h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center">
                    <FileText className="h-6 w-6 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {dragActive ? 'Drop PDF file here' : 'Drag and drop PDF file here'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">or click to browse</p>
                  </div>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload"
                    disabled={uploadingFile}
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition-colors text-sm disabled:opacity-50"
                  >
                    {uploadingFile ? 'Uploading...' : 'Choose File'}
                  </label>
                </div>
              </div>

              <div className="mt-4 text-xs text-gray-500">
                <p>• Only PDF files are accepted</p>
                <p>• Maximum file size: 10MB</p>
                <p>• Document will be set to "Pending" approval status</p>
              </div>

              {uploadingFile && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className="text-sm text-blue-800">Uploading SWMS document...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SWMS Signatures Modal */}
      {showSignaturesModal && selectedSWMSForSignatures && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">SWMS Worker Signatures</h3>
                  <p className="text-sm text-gray-500">{selectedSWMSForSignatures.title}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {swmsSignatures.length > 0 && (
                  <button
                    onClick={generateSignaturesPDF}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    <span>Download Report</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowSignaturesModal(false);
                    setSelectedSWMSForSignatures(null);
                    setSWMSSignatures([]);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* SWMS Information */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-blue-900 mb-2">SWMS Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-blue-800">Site:</span>
                    <span className="text-blue-700 ml-2">{selectedSWMSForSignatures.site?.name}</span>
                  </div>
                  <div>
                    <span className="font-medium text-blue-800">Status:</span>
                    <span className="text-blue-700 ml-2 capitalize">{selectedSWMSForSignatures.approval_status}</span>
                  </div>
                  <div>
                    <span className="font-medium text-blue-800">Version:</span>
                    <span className="text-blue-700 ml-2">v{selectedSWMSForSignatures.version}</span>
                  </div>
                  <div>
                    <span className="font-medium text-blue-800">Created:</span>
                    <span className="text-blue-700 ml-2">{new Date(selectedSWMSForSignatures.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="mt-3">
                  <span className="font-medium text-blue-800">Activity:</span>
                  <p className="text-blue-700 text-sm mt-1">{selectedSWMSForSignatures.activity_description}</p>
                </div>
              </div>

              {/* Worker Signatures */}
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-medium text-gray-900">Worker Acknowledgments</h4>
                    <span className="text-sm text-gray-500">
                      {loadingSignatures ? 'Loading...' : `${swmsSignatures.length} signatures`}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  {loadingSignatures ? (
                    <div className="flex items-center justify-center p-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                      <span className="ml-3 text-gray-600">Loading signatures...</span>
                    </div>
                  ) : swmsSignatures.length > 0 ? (
                    <div className="space-y-4">
                      {swmsSignatures.map((signature, index) => (
                        <div key={signature.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center space-x-4">
                              <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                                <span className="text-sm font-bold text-green-700">{index + 1}</span>
                              </div>
                              <div>
                                <h5 className="font-medium text-gray-900">{signature.workers?.name}</h5>
                                <p className="text-sm text-gray-600 capitalize">{signature.workers?.role?.replace('_', ' ')}</p>
                                <p className="text-xs text-gray-500">{signature.workers?.email}</p>
                                {signature.workers?.whitecard_number && (
                                  <p className="text-xs text-gray-500">White Card: {signature.workers.whitecard_number}</p>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-gray-900">
                                {signature.signed_at ? new Date(signature.signed_at).toLocaleDateString() : 'Not signed'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {signature.signed_at ? new Date(signature.signed_at).toLocaleTimeString() : ''}
                              </p>
                            </div>
                          </div>
                          
                          {signature.signature && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700 flex items-center">
                                  <PenTool className="h-4 w-4 mr-2" />
                                  Digital Signature:
                                </span>
                                <div className="bg-white border border-gray-300 rounded-md p-2 max-w-xs">
                                  {signature.signature.startsWith('data:image') ? (
                                    <img 
                                      src={signature.signature} 
                                      alt="Worker signature" 
                                      className="max-w-full h-auto max-h-12"
                                    />
                                  ) : (
                                    <p className="text-sm text-gray-900 font-serif italic">
                                      {signature.signature}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Signatures Found</h3>
                      <p className="text-gray-500">No workers have signed this SWMS yet.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {swmsList.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">SWMS Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {swmsList.length}
              </div>
              <div className="text-sm text-gray-600">Total SWMS</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {swmsList.filter(s => s.approval_status === 'approved').length}
              </div>
              <div className="text-sm text-gray-600">Approved</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {swmsList.filter(s => s.approval_status === 'pending').length}
              </div>
              <div className="text-sm text-gray-600">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-600">
                {swmsList.filter(s => s.approval_status === 'draft').length}
              </div>
              <div className="text-sm text-gray-600">Draft</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};